import { request } from "utils/request"
import { uploadFilesToS3 } from "utils/s3Upload"

// Presigned-POST seam for LIMS document file bytes (PRD-003 Area 2 / SPEC-008; SPEC-021 B6:
// the localStorage mock branch is gone — this is the REAL seam).
//
// The flow mirrors Librarian's upload path: ask the backend for a presigned POST, upload the
// bytes straight to S3 with the existing `uploadFilesToS3` helper, then persist only the stable
// storage key + file metadata. The SERVER ALWAYS MINTS the team-scoped object key
// (`<area>/<team_id>/<uuid>-<safe_name>`) — the client NEVER sends a key (SPEC-007 D9: a
// client-chosen key is a cross-tenant overwrite vector; the backend ignores one anyway) and
// persists the `key` echoed in the response.
//
// Kept SEPARATE from limsForskrifter so Area 4 (QA report files) reuses the same seam with its
// own prefix → its own endpoint + bucket. ⚠ Live uploads require the S3 buckets + AWS creds
// (LIMS_FORSKRIFTER_BUCKET / LIMS_QAREPORTS_BUCKET, eu-central-1) to be provisioned; an unset
// bucket is a clean backend 500.
//
// NB: there is no backend presigned-GET/download endpoint yet — stored files carry only their
// key/metadata and the viewer's open/download actions stay disabled until SP-E exposes one.

// Upload-URL endpoint per storage area. The `prefix` names the consuming area, exactly as the
// mock seam did, so callers are unchanged.
const UPLOAD_URL_PATHS = {
  forskrifter: "/lims/forskrifter/get-upload-url",
  qaReports: "/lims/qaReports/get-upload-url",
}

/**
 * Upload one document file through the presigned-POST path and resolve to a stable file
 * descriptor `{ key, name, contentType, size }` the metadata slice stores on a version /
 * QA decision. The `key` is the SERVER-minted team-scoped storage key.
 *
 * @param {File} file        the dropped/selected file
 * @param {object} [opts]
 * @param {string} [opts.prefix="forskrifter"]  storage area ("forskrifter" | "qaReports")
 * @param {(pct:number)=>void} [opts.onProgress]
 * @returns {Promise<{key,name,contentType,size}>}
 * @throws if the presign call or the upload fails — callers surface a message + store nothing.
 */
export async function uploadDocumentFile(file, { prefix = "forskrifter", onProgress } = {}) {
  if (!file) throw new Error("uploadDocumentFile: no file")
  const path = UPLOAD_URL_PATHS[prefix] || UPLOAD_URL_PATHS.forskrifter
  const res = await request("POST", path, {
    input: { fileName: file.name, contentType: file.type || null },
  })
  const failed = await uploadFilesToS3([file], [res.presignedPost], { onProgress })
  if (failed.length > 0) throw new Error("uploadDocumentFile: upload failed")
  return { key: res.key, name: file.name, contentType: file.type || null, size: file.size || null }
}
